import { useState, useCallback, useEffect } from 'react'
import { getApiBaseUrl } from './useFunnelConfig'

export interface WishlistProductImage {
  wcId: number
  image: string
  layer: number
}

export interface WishlistItem {
  id: string
  email: string
  funnel_id: string
  funnel_slug?: string // For cross-configurator navigation
  funnel_size?: string // Size of the funnel (s, m, l) for proper image display
  base_image?: string // Base image from the funnel
  product_images?: WishlistProductImage[] // Product images in the correct size for this wishlist
  config: number[]
  total_price: number
  name: string | null
  created_at: string
  updated_at: string
  has_unavailable_products?: boolean // True if some products no longer exist
}

const WISHLIST_EMAIL_KEY = 'wishlist_email'
const WISHLIST_SETTINGS_KEY = 'configurator_settings'
const MAX_WISHLIST_ITEMS = 30

export function useWishlist(funnelId: string | undefined, currentSlug?: string) {
  const [email, setEmail] = useState<string | null>(null)
  const [wishlists, setWishlists] = useState<WishlistItem[]>([])
  const [loading, setLoading] = useState(false)
  const [showEmailPopup, setShowEmailPopup] = useState(false)
  const [showFullPopup, setShowFullPopup] = useState(false)

  // Load email from localStorage on mount
  useEffect(() => {
    const savedEmail = localStorage.getItem(WISHLIST_EMAIL_KEY)
    if (savedEmail) {
      setEmail(savedEmail)
    }
  }, [])

  const loadWishlists = useCallback(async () => {
    if (!email) return

    setLoading(true)
    try {
      // Get excluded funnels from settings
      let excludeFunnels = ''
      const settingsStr = localStorage.getItem(WISHLIST_SETTINGS_KEY)
      if (settingsStr) {
        try {
          const settings = JSON.parse(settingsStr)
          if (settings.excludedWishlistFunnels?.length > 0) {
            excludeFunnels = settings.excludedWishlistFunnels.join(',')
          }
        } catch (e) {
          // Ignore
        }
      }

      // Fetch all wishlists (cross-funnel)
      const baseUrl = getApiBaseUrl()
      let url = `${baseUrl}/api/wishlist?email=${encodeURIComponent(email)}`
      if (excludeFunnels) {
        url += `&excludeFunnels=${excludeFunnels}`
      }

      const res = await fetch(url)
      if (res.ok) {
        const { wishlists: items } = await res.json()
        setWishlists(items || [])
      }
    } catch (e) {
      console.error('Failed to load wishlists:', e)
    } finally {
      setLoading(false)
    }
  }, [email])

  // Load wishlists when email is set (cross-funnel)
  useEffect(() => {
    if (email) {
      loadWishlists()
    }
  }, [email, loadWishlists])

  // Called when user clicks heart without email set
  const requestEmail = useCallback(() => {
    setShowEmailPopup(true)
  }, [])

  // Called when user submits email in popup
  const submitEmail = useCallback((newEmail: string) => {
    const normalized = newEmail.toLowerCase().trim()
    localStorage.setItem(WISHLIST_EMAIL_KEY, normalized)
    setEmail(normalized)
    setShowEmailPopup(false)
    return normalized
  }, [])

  // Add new config to wishlist
  // overrideFunnelId allows saving to a different funnel (e.g. from Swiper likes drawer)
  const addToWishlist = useCallback(async (config: number[], totalPrice: number, overrideFunnelId?: string) => {
    const targetFunnelId = overrideFunnelId || funnelId
    if (!email || !targetFunnelId) return false

    // Check if wishlist is full
    if (wishlists.length >= MAX_WISHLIST_ITEMS) {
      setShowFullPopup(true)
      return false
    }

    setLoading(true)
    try {
      const baseUrl = getApiBaseUrl()
      const res = await fetch(`${baseUrl}/api/wishlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          funnelId: targetFunnelId,
          config,
          totalPrice,
          addNew: true, // Flag to add as new item
        }),
      })

      if (res.ok) {
        const { wishlist: savedWishlist } = await res.json()
        setWishlists(prev => [savedWishlist, ...prev])
        return true
      }
    } catch (e) {
      console.error('Failed to save wishlist:', e)
    } finally {
      setLoading(false)
    }
    return false
  }, [email, funnelId, wishlists.length])

  // Delete a wishlist item
  const deleteWishlistItem = useCallback(async (itemId: string) => {
    if (!email) return false

    try {
      const baseUrl = getApiBaseUrl()
      const res = await fetch(`${baseUrl}/api/wishlist?id=${itemId}&email=${encodeURIComponent(email)}`, {
        method: 'DELETE',
      })

      if (res.ok) {
        setWishlists(prev => prev.filter(w => w.id !== itemId))
        return true
      }
    } catch (e) {
      console.error('Failed to delete wishlist:', e)
    }
    return false
  }, [email])

  // Clear wishlist email (logout)
  const clearEmail = useCallback(() => {
    localStorage.removeItem(WISHLIST_EMAIL_KEY)
    setEmail(null)
    setWishlists([])
  }, [])

  return {
    email,
    wishlists,
    loading,
    showEmailPopup,
    setShowEmailPopup,
    showFullPopup,
    setShowFullPopup,
    requestEmail,
    submitEmail,
    addToWishlist,
    deleteWishlistItem,
    loadWishlists,
    clearEmail,
    hasEmail: !!email,
    maxItems: MAX_WISHLIST_ITEMS,
    isFull: wishlists.length >= MAX_WISHLIST_ITEMS,
  }
}
